import { Loader2, Scissors, Square, Eye } from "lucide-react";

export default function VariantAccordion({
  variants,
  productIndex,
  processingVariantId,
  processingBackground,
  onPreviewVariant,
  onDownloadVariant,
}) {
  if (!variants || variants.length === 0) return null;

  return (
    <tr className="bg-[#111111]/50">
      <td colSpan="6" className="p-4">
        <div className="ml-12">
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">VARIANTS</div>
          <div className="space-y-2">
            {variants.map((variant, vIdx) => {
              const variantKey = `variant-${productIndex}-${vIdx}`;
              const imageCount = variant.images?.length || 0;
              const isProcessing = processingVariantId && String(processingVariantId) === String(variant.variant_id);
              const isRenderingWhite = isProcessing && processingBackground === "white";
              const isCuttingTransparent = isProcessing && processingBackground === "transparent";
              const labelName = (variant.title || "").toUpperCase();

              return (
                <div
                  key={variantKey}
                  className="flex items-center gap-4 p-3 bg-[#0A0A0A] border border-[#27272A]"
                  data-testid={variantKey}
                >
                  {variant.image_url && (
                    <img
                      src={variant.image_url}
                      alt={variant.title}
                      className="w-10 h-10 object-cover border border-[#27272A]"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-mono truncate">{variant.title}</div>
                    {variant.sku && (
                      <div className="text-xs text-zinc-500 font-mono truncate">SKU: {variant.sku}</div>
                    )}
                    {imageCount > 0 && (
                      <div
                        className="text-[10px] text-zinc-600 font-mono mt-0.5"
                        data-testid={`variant-delivery-hint-${productIndex}-${vIdx}`}
                      >
                        {imageCount === 1
                          ? "↓ 1 file"
                          : imageCount <= 9
                          ? `↓ ${imageCount} loose files`
                          : "↓ ZIP"}
                      </div>
                    )}
                  </div>

                  {imageCount > 0 && onPreviewVariant && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => onPreviewVariant(variant, "white")}
                        disabled={isProcessing}
                        className="bg-transparent border border-[#00FFFF]/50 text-[#00FFFF] hover:bg-[#00FFFF] hover:text-black font-bold text-[10px] uppercase tracking-wider px-3 py-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 whitespace-nowrap"
                        data-testid={`variant-preview-btn-${productIndex}-${vIdx}`}
                        title={`Preview which ${variant.title} images will get cut out`}
                      >
                        <Eye className="w-3 h-3 shrink-0" />
                        PREVIEW {labelName} CUT-OUTS
                      </button>

                      <button
                        onClick={() => onDownloadVariant(variant, "transparent")}
                        disabled={isProcessing}
                        className="bg-[#FF00CC]/10 border border-[#FF00CC] text-[#FF8AE2] hover:bg-[#FF00CC] hover:text-black font-bold text-[10px] uppercase tracking-wider px-3 py-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 whitespace-nowrap"
                        data-testid={`variant-transparent-btn-${productIndex}-${vIdx}`}
                        title={`Cut out ${variant.title} product shots to transparent PNGs`}
                      >
                        {isCuttingTransparent ? (
                          <><Loader2 className="w-3 h-3 animate-spin" />CUTTING OUT...</>
                        ) : (
                          <><Scissors className="w-3 h-3 shrink-0" />GET {labelName} (TRANS BG)</>
                        )}
                      </button>

                      <button
                        onClick={() => onDownloadVariant(variant, "white")}
                        disabled={isProcessing}
                        className="bg-white/10 border border-white/60 text-white hover:bg-white hover:text-black font-bold text-[10px] uppercase tracking-wider px-3 py-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 whitespace-nowrap"
                        data-testid={`variant-white-btn-${productIndex}-${vIdx}`}
                        title={`Render ${variant.title} product shots on clean white`}
                      >
                        {isRenderingWhite ? (
                          <><Loader2 className="w-3 h-3 animate-spin" />RENDERING...</>
                        ) : (
                          <><Square className="w-3 h-3 shrink-0" />GET {labelName} (WHITE BG)</>
                        )}
                      </button>
                    </div>
                  )}

                  {variant.price && (
                    <div className="text-sm text-zinc-400 font-mono shrink-0">${variant.price}</div>
                  )}
                  <div className={`text-xs font-mono uppercase shrink-0 ${variant.available ? 'text-[#39FF14]' : 'text-red-500'}`}>
                    {variant.available ? 'IN STOCK' : 'OUT OF STOCK'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </td>
    </tr>
  );
}
